import { useState, type ChangeEvent } from "react";
import { Input } from "@design-system/Input/Input";
import type {
    InputForcedState,
    InputSize,
    InputVariant,
} from "@design-system/Input/Input.types";
import { SearchIcon } from "@icons";
import {
    defaultInputSize,
    sizeOptionsByVariant,
    toFontSize,
} from "./InputPlayground.lib";
import type { InputTextSize } from "./InputPlayground.lib";
import {
    InputPlaygroundControls,
    type InputFeatureFlags,
} from "./InputPlaygroundControls";
import { InputMatrix } from "./InputPlaygroundMatrix";
import {
    useAnatomyMode,
    type AnatomyForcedState,
} from "./componentAnatomyMode";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

/** Input has no pressed state, so that one state drops on the way through. */
const toInputForcedState = (
    state: AnatomyForcedState | undefined,
): InputForcedState | undefined => {
    switch (state) {
        case "hover":
        case "focus":
            return state;
        default:
            return undefined;
    }
};

/**
 * Sandbox for the literal Figma matrix. Illegal combinations are legalised
 * here (ghost/accent stay at md, 12px text is ghost-only) instead of being
 * reachable, so the stage only ever shows what the types allow.
 */
export const InputPlayground = () => {
    const [variant, setVariant] = useState<InputVariant>("primary");
    const [size, setSize] = useState<InputSize>(defaultInputSize);
    const [textSize, setTextSize] = useState<InputTextSize>("14");
    const [features, setFeatures] = useState<InputFeatureFlags>({
        hasLeftIcon: true,
        isClearable: false,
        isDisabled: false,
        isReadOnly: false,
        isInvalid: false,
    });
    const [value, setValue] = useState("Input");

    const { hasLeftIcon, isClearable, isDisabled, isReadOnly, isInvalid } =
        features;

    const sizeOptions: readonly InputSize[] = sizeOptionsByVariant[variant];

    const handleVariantChange = (nextVariant: InputVariant) => {
        setVariant(nextVariant);

        const nextOptions: readonly InputSize[] =
            sizeOptionsByVariant[nextVariant];

        if (!nextOptions.some((option) => option === size)) {
            setSize(nextOptions[0]);
        }
    };

    const isSmallText = variant === "ghost" && textSize === "12";
    const sizeId = isSmallText ? "md-xs" : size;
    const anatomy = useAnatomyMode({
        component: "input",
        variantId:
            variant === "primary" && isInvalid ? "primary-invalid" : variant,
        sizeId,
        isDisabled,
        isReadOnly,
        inputContent: {
            text: value,
            placeholder: "Placeholder",
            hasLeftIcon,
            isClearable,
        },
    });

    const forcedState = toInputForcedState(anatomy.forcedState);
    const shared = {
        leftIcon: hasLeftIcon ? <SearchIcon /> : undefined,
        disabled: isDisabled,
        readOnly: isReadOnly,
        value,
        placeholder: "Placeholder",
        "data-forced-state": forcedState,
        onChange: (event: ChangeEvent<HTMLInputElement>) =>
            setValue(event.target.value),
    };

    const renderStage = () => {
        if (variant === "ghost") {
            return (
                <Input
                    variant="ghost"
                    fontSize={toFontSize(textSize)}
                    clearable={isClearable}
                    {...shared}
                />
            );
        }

        if (variant === "accent") {
            return (
                <Input variant="accent" clearable={isClearable} {...shared} />
            );
        }

        return (
            <Input
                size={size}
                invalid={isInvalid}
                clearable={isClearable}
                {...shared}
            />
        );
    };

    const summary = `${variant} · ${size}`;
    const xraySummary = `x-ray · ${variant} · ${sizeId}`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <InputPlaygroundControls
                        variant={variant}
                        onVariantChange={handleVariantChange}
                        size={size}
                        sizeOptions={sizeOptions}
                        onSizeChange={setSize}
                        textSize={textSize}
                        onTextSizeChange={setTextSize}
                        features={features}
                        onFeaturesChange={setFeatures}
                    />
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? xraySummary : summary}
                belowStage={<InputMatrix />}
                output={JSON.stringify(
                    {
                        variant,
                        size,
                        fontSize:
                            variant === "ghost" ? toFontSize(textSize) : 14,
                        clearable: isClearable,
                        disabled: isDisabled,
                        readOnly: isReadOnly,
                        invalid: variant === "primary" ? isInvalid : undefined,
                        value,
                        forcedState: forcedState ?? null,
                    },
                    null,
                    2,
                )}
            >
                {anatomy.view ?? (
                    <div style={{ width: 240 }}>{renderStage()}</div>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
